/**
 * Utility functions for handling message redaction display logic
 */

/**
 * Get redaction information for displaying who deleted a message
 * @param {object} message - Matrix event object
 * @param {Array} members - Array of room members with id, userId, and name
 * @param {object} client - Matrix client instance
 * @returns {object} Redaction info including redactor details
 */
export const getRedactionInfo = (message, members, client) => {
  if (!message?.isRedacted || !message.isRedacted()) {
    return null;
  }

  // Get redaction info from unsigned data (always available per Matrix spec)
  const redactionInfo = message.getUnsigned()?.redacted_because;
  const redactorId = redactionInfo?.sender;
  const originalSenderId = message.getSender();
  const currentUserId = client?.getUserId();

  // Handle case where redaction info is missing
  if (!redactorId) {
    return null;
  }

  // Find the redactor in members list for display name
  // Members might have either 'id' or 'userId' or both, so check both
  // Ensure members is an array to prevent errors
  const redactorMember = Array.isArray(members)
    ? members.find(m => m.userId === redactorId || m.id === redactorId)
    : null;
  const redactorName = redactorMember?.name || redactorId;

  // Validate currentUserId and originalSenderId to prevent false matches with undefined
  const isCurrentUser = currentUserId && redactorId === currentUserId;
  const isOriginalSender = originalSenderId && redactorId === originalSenderId;

  return {
    redactorId,
    redactorName,
    isCurrentUser,
    isOriginalSender,
    deletedByCurrentUser: isCurrentUser && isOriginalSender,
    deletedByModeratorCurrentUser: isCurrentUser && !isOriginalSender,
    deletedByOriginalSender: !isCurrentUser && isOriginalSender,
    deletedByModerator: !isCurrentUser && !isOriginalSender,
    deletedAt: redactionInfo?.origin_server_ts || null,
    reason: redactionInfo?.content?.reason || null,
  };
};

/**
 * Get the appropriate deletion message translation key based on who deleted it
 * @param {object} redactionInfo - Info from getRedactionInfo()
 * @param {boolean} isUserMessage - Whether the message was sent by current user
 * @returns {object} Translation key and params
 */
export const getRedactionTranslation = (redactionInfo, isUserMessage) => {
  // No redaction info available - fall back to generic message
  if (!redactionInfo || !redactionInfo.redactorId) {
    return {
      key: isUserMessage ? 'messageDeletedByYou' : 'messageDeletedBySender',
      params: {},
    };
  }

  // Current user deleted their own message
  if (redactionInfo.deletedByCurrentUser) {
    return {
      key: 'messageDeletedByYou',
      params: {},
    };
  }

  // Current user (moderator) deleted someone else's message
  if (redactionInfo.deletedByModeratorCurrentUser) {
    return {
      key: 'youDeletedMessage',
      params: {},
    };
  }

  // Original sender deleted their own message
  if (redactionInfo.deletedByOriginalSender) {
    return {
      key: 'messageDeletedBySender',
      params: {},
    };
  }

  // Someone else (moderator) deleted this message
  return {
    key: 'messageDeletedByModerator',
    params: { moderatorName: redactionInfo.redactorName || 'Unknown' },
  };
};
